import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, getDeviceFingerprint } from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Car, ArrowRight, ShieldCheck } from "lucide-react";

export default function Scan() {
  const { cardCode } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const meta = await api.get(`/cards/${cardCode}`);
        setCard(meta.data);

        let gps = {};
        try {
          gps = await new Promise((resolve) => {
            if (!navigator.geolocation) return resolve({});
            navigator.geolocation.getCurrentPosition(
              (p) => resolve({ gps_lat: p.coords.latitude, gps_lng: p.coords.longitude }),
              () => resolve({}),
              { timeout: 5000 }
            );
          });
        } catch { /* ignore */ }

        const { data } = await api.post(`/scan/${cardCode}`, {
          device_fingerprint: getDeviceFingerprint(),
          user_agent: navigator.userAgent,
          ...gps,
        });
        setSession(data);
        sessionStorage.setItem("qaab_scan_session", JSON.stringify({ ...data, gps }));
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [cardCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center px-6">
        <div className="text-center">
          <ScanLine size={32} className="mx-auto text-[#002FA7] animate-pulse" />
          <div className="font-display font-bold mt-4 text-xl">Reading card…</div>
          <div className="font-mono text-xs text-gray-500 mt-1">{cardCode}</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="card-flat p-6 border-[#E63946]" data-testid="scan-error">
            <div className="label-eyebrow text-[#E63946]">Cannot proceed</div>
            <h1 className="font-display font-black text-2xl mt-2 mb-3">Access restricted</h1>
            <p className="text-gray-700 text-sm">{err}</p>
          </div>
          <Link to="/" className="mt-4 text-sm text-gray-500 inline-block">← Back to qaab.id</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      {/* Top header */}
      <header className="bg-[#111827] text-white py-4">
        <div className="max-w-md mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white text-[#002FA7] grid place-items-center font-display font-extrabold text-xs">Q</div>
            <span className="font-display font-extrabold tracking-tight text-sm">QAAB</span>
          </Link>
          <span className="font-mono text-[10px] text-gray-400">{cardCode}</span>
        </div>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 py-8">
        <div className="label-eyebrow mb-3">You scanned a Qaab card</div>
        <h1 className="font-display font-black text-3xl tracking-tight mb-2">Need to reach the driver?</h1>
        <p className="text-gray-600 mb-6 leading-relaxed">Pick the reason below. We'll send them a private alert — without sharing their number with you.</p>

        <div className="card-flat p-4 mb-6 flex items-center gap-4" data-testid="vehicle-info">
          <div className="w-12 h-12 bg-[#111827] text-white grid place-items-center">
            <Car size={20} />
          </div>
          <div>
            <div className="font-display font-bold">{card?.vehicle?.nickname}</div>
            <div className="font-mono text-xs text-gray-500">{card?.vehicle?.plate_number}</div>
          </div>
          <div className="ml-auto inline-flex items-center gap-1 text-xs font-mono text-[#10B981]">
            <ShieldCheck size={12} /> active
          </div>
        </div>

        <div className="space-y-3" data-testid="reasons-list">
          <ReasonButton
            urgent
            title="Blocked parking"
            desc="Their car is blocking you. Identity required."
            onClick={() => nav(`/s/${cardCode}/report?reason=blocked`)}
            testId="reason-blocked"
          />
          <ReasonButton
            urgent
            title="Emergency"
            desc="Accident, fire, or urgent danger. Identity required."
            onClick={() => nav(`/s/${cardCode}/report?reason=emergency`)}
            testId="reason-emergency"
          />
          <ReasonButton
            title="Lights left on"
            desc="Their headlights are on. Anonymous OK."
            onClick={() => nav(`/s/${cardCode}/report?reason=lights`)}
            testId="reason-lights"
          />
          <ReasonButton
            title="Window open"
            desc="A window or sunroof is open. Anonymous OK."
            onClick={() => nav(`/s/${cardCode}/report?reason=window`)}
            testId="reason-window"
          />
          <ReasonButton
            title="Flat tyre"
            desc="One of their tyres is flat. Anonymous OK."
            onClick={() => nav(`/s/${cardCode}/report?reason=tyre`)}
            testId="reason-tyre"
          />
          <ReasonButton
            title="Scratch / damage"
            desc="You notice damage. Scene photo helpful."
            onClick={() => nav(`/s/${cardCode}/report?reason=scratch`)}
            testId="reason-scratch"
          />
        </div>

        <p className="text-xs text-gray-500 mt-8 leading-relaxed">
          By continuing you agree to identify yourself if reporting a serious situation. False reports are tracked and may result in a permanent ban.
        </p>
      </main>
    </div>
  );
}

function ReasonButton({ title, desc, urgent, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full p-5 border text-left flex items-center justify-between gap-4 transition-all min-h-[72px] ${
        urgent ? "border-[#E63946] bg-white hover:bg-[#E63946]/5" : "border-gray-200 bg-white hover:border-gray-400"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className={`font-display font-bold text-lg ${urgent ? "text-[#E63946]" : ""}`}>{title}</div>
        <div className="text-sm text-gray-600 mt-0.5">{desc}</div>
      </div>
      <ArrowRight size={18} className={urgent ? "text-[#E63946]" : "text-gray-400"} />
    </button>
  );
}
